import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { ArrowRight, CheckCircle, XCircle, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";

const CSATPracticeMode = ({ 
  isPro, 
  onBack,
  learningTopics,
  setLearningTopics 
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Topic selection
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  
  // Practice state
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSolution, setShowSolution] = useState(false);
  const [score, setScore] = useState(null);

  // Load practice questions
  const loadPracticeQuestions = async (topicId, subtopicId) => {
    setLoading(true);
    toast({ 
      description: "🔄 Generating practice questions... This may take 15-20 seconds.",
      duration: 15000 
    });
    
    try {
      const startTime = Date.now();
      const { data } = await axios.get(
        `${API_BASE}/csat/practice/${topicId}/${subtopicId}?count=30`
      );
      const loadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      
      setPracticeQuestions(data.questions || []);
      setCurrentQ(0);
      setAnswers({});
      setShowSolution(false);
      setScore(null);
      
      const cacheMsg = loadTime < 1 ? " (⚡ Cached)" : " (Generated fresh)";
      toast({ description: `✅ ${data.total_questions} questions loaded in ${loadTime}s${cacheMsg}` });
    } catch (err) {
      toast({ 
        variant: "destructive", 
        description: err.response?.data?.detail || "Could not load practice questions." 
      });
    } finally {
      setLoading(false);
    }
  };

  // Load topics if needed
  const loadTopics = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/csat/learning/topics`);
      setLearningTopics(data.topics || []);
      toast({ description: "✅ Topics loaded successfully!" });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load topics." });
    } finally {
      setLoading(false);
    }
  };

  // Submit practice
  const submitPractice = () => {
    let correct = 0;
    practiceQuestions.forEach((q, idx) => {
      if (answers[idx] === q.correct_answer) correct++;
    });
    setScore({
      correct,
      total: practiceQuestions.length,
      percentage: ((correct / practiceQuestions.length) * 100).toFixed(1)
    });
    toast({ description: `🎯 Score: ${correct}/${practiceQuestions.length}` });

    // ═══════════════════════════════════════════════════════════════════
    // SINGLE FRONT DOOR — log this CSAT practice attempt to Strategy Analytics
    // ═══════════════════════════════════════════════════════════════════
    const fdToken = localStorage.getItem('token');
    if (fdToken) {
      const loggedQuestions = practiceQuestions.map((q, idx) => ({
        sub_topic: q.sub_topic || selectedSubtopic || q.topic || 'unknown',
        is_correct: answers[idx] ? answers[idx] === q.correct_answer : null,
        difficulty: q.difficulty || 'medium',
        time_seconds: 0,
      }));
      axios.post(`${API_BASE}/csat/attempt/log`, {
        source: 'csat_practice',
        section: selectedTopic,
        questions: loggedQuestions,
      }, { headers: { Authorization: `Bearer ${fdToken}` } }).catch(() => {});
    }

    // ═══════════════════════════════════════════════════════════════════
    // TRACK WRONG ANSWERS TO MISTAKE BANK
    // ═══════════════════════════════════════════════════════════════════
    const token = localStorage.getItem('token');
    if (token) {
      practiceQuestions.forEach(async (q, idx) => {
        const userAnswer = answers[idx];
        if (userAnswer && userAnswer !== q.correct_answer) {
          try {
            await axios.post(`${API_BASE}/mistakes/add`, {
              question_id: `csat-practice-${selectedTopic}-${Date.now()}-${idx}`,
              question: q.question,
              options: q.options,
              correct_option: ['A', 'B', 'C', 'D'].indexOf(q.correct_answer),
              explanation: q.explanation || '',
              user_answer: ['A', 'B', 'C', 'D'].indexOf(userAnswer),
              source: 'csat_practice',
              subject: `CSAT - ${selectedTopic}`
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            // Silent fail
          }
        }
      });
    }
  };

  const currentQuestion = practiceQuestions[currentQ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" className="border-slate-200" onClick={() => {
          if (practiceQuestions.length > 0) {
            setPracticeQuestions([]);
            setSelectedSubtopic(null);
          } else if (selectedTopic) {
            setSelectedTopic(null);
          } else {
            onBack();
          }
        }}>
          ← Back
        </Button>
        <Badge variant="outline" className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}>{isPro ? "PRO" : "FREE"}</Badge>
      </div>

      {/* Header - CLARIAS Style */}
      <div className="pb-6 border-b border-slate-100">
        <h1 className="text-2xl font-semibold text-slate-900">Practice Mode</h1>
        <p className="text-slate-500">
          30+ unique questions per topic • Fresh questions every time
        </p>
      </div>

      {/* Topic Selection - CLARIAS Style */}
      {!selectedTopic && (
        <div className="space-y-6">
          <h2 className="text-base font-medium text-slate-700">Select a Topic to Practice</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {loading ? (
              <div className="text-center text-slate-600 col-span-2 py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mb-3"></div>
                <p>Loading topics...</p>
              </div>
            ) : learningTopics.length === 0 ? (
              <div className="text-center col-span-2 py-8">
                <p className="text-slate-600 mb-4">Topics not loaded</p>
                <Button onClick={loadTopics} className="bg-slate-900 hover:bg-slate-800">
                  Load Topics
                </Button>
              </div>
            ) : (
              learningTopics.map((topic) => (
                <Card 
                  key={topic.id} 
                  className="group cursor-pointer border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
                  onClick={() => setSelectedTopic(topic)}
                >
                  <CardContent className="p-5">
                    <h3 className="font-medium text-slate-900 mb-2">{topic.name}</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {topic.subtopic_count} subtopics available
                    </p>
                    <div className="flex items-center text-xs text-slate-400 group-hover:text-slate-600">
                      Practice now
                      <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}

      {/* Subtopic Selection */}
      {selectedTopic && !selectedSubtopic && !loading && practiceQuestions.length === 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-slate-900">{selectedTopic.name} - Select Subtopic</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {selectedTopic.subtopics.map((subtopic) => (
              <Card 
                key={subtopic.id}
                className="cursor-pointer hover:shadow-sm transition-all border-slate-200 hover:border-slate-300"
                onClick={() => {
                  setSelectedSubtopic(subtopic);
                  loadPracticeQuestions(selectedTopic.id, subtopic.id);
                }}
              >
                <CardContent className="p-4">
                  <h3 className="font-bold text-emerald-900 mb-2">{subtopic.name}</h3>
                  <Badge variant="outline" className="text-xs">{subtopic.difficulty}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && selectedSubtopic && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4"></div>
          <p className="text-lg font-semibold text-emerald-900">Generating Practice Questions</p>
          <p className="text-sm text-slate-600">This may take 15-30 seconds for first load...</p>
        </div>
      )}

      {/* Practice Questions */}
      {practiceQuestions.length > 0 && currentQuestion && (
        <div className="space-y-6">
          {/* Question Navigator */}
          <div className="flex flex-wrap gap-2 justify-center">
            {practiceQuestions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setCurrentQ(idx); setShowSolution(false); }}
                className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
                  currentQ === idx
                    ? "bg-emerald-600 text-white"
                    : answers[idx]
                      ? score
                        ? answers[idx] === practiceQuestions[idx].correct_answer
                          ? "bg-green-200 text-green-800"
                          : "bg-red-200 text-red-800"
                        : "bg-emerald-100 text-emerald-800"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {/* Question Display — keyed by currentQ so it fully remounts on
              navigation, preventing Google Translate stale option text. */}
          <Card key={currentQ} className="border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Badge className="bg-emerald-100 text-emerald-800">
                  Question {currentQ + 1} / {practiceQuestions.length}
                </Badge>
                <Badge variant="outline">
                  {currentQuestion.difficulty || "Medium"}
                </Badge>
              </div>

              <p className="text-lg font-medium text-slate-900 mb-6">
                {currentQuestion.question}
              </p>

              {/* Options */}
              <div className="space-y-3">
                {currentQuestion.options?.map((option, optIdx) => {
                  const optionLetter = option.charAt(0);
                  const isSelected = answers[currentQ] === optionLetter;
                  const isCorrect = optionLetter === currentQuestion.correct_answer;
                  const showResult = score || showSolution;

                  return (
                    <button
                      key={optIdx}
                      onClick={() => {
                        if (!score) {
                          setAnswers({ ...answers, [currentQ]: optionLetter });
                        }
                      }}
                      disabled={!!score}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        showResult && isCorrect
                          ? "border-green-500 bg-green-50"
                          : showResult && isSelected && !isCorrect
                            ? "border-red-500 bg-red-50"
                            : isSelected
                              ? "border-emerald-500 bg-emerald-50"
                              : "border-slate-200 hover:border-emerald-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{option}</span>
                        {showResult && isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                        {showResult && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Solution Toggle */}
              {answers[currentQ] && !score && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowSolution(!showSolution)}
                >
                  {showSolution ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {showSolution ? "Hide" : "Show"} Solution
                </Button>
              )}

              {/* Explanation */}
              {(showSolution || score) && currentQuestion.explanation && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs font-semibold text-blue-700 mb-2">EXPLANATION</p>
                  <p className="text-sm text-slate-800">{currentQuestion.explanation}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation & Submit */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => { setCurrentQ(Math.max(0, currentQ - 1)); setShowSolution(false); }}
              disabled={currentQ === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>

            {!score && (
              <Button
                onClick={submitPractice}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={Object.keys(answers).length < practiceQuestions.length}
              >
                Submit Test ({Object.keys(answers).length}/{practiceQuestions.length})
              </Button>
            )}

            {score && (
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-900">
                  Score: {score.correct}/{score.total} ({score.percentage}%)
                </p>
              </div>
            )}

            <Button
              variant="outline"
              onClick={() => { setCurrentQ(Math.min(practiceQuestions.length - 1, currentQ + 1)); setShowSolution(false); }}
              disabled={currentQ === practiceQuestions.length - 1}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default CSATPracticeMode;
